import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Toaster from 'components/Toaster';
import { CREATE_STRIPE_ONBOARDING_LINK } from 'components/PromoterOnboardingSuccessModal/gql';
import { AppContext } from 'shared/AppContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${(props) =>
    props.theme?.colors?.background?.primary ||
    (props.theme?.name === 'dark'
      ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
      : 'rgb(248, 250, 252)')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme?.spacing?.xl || '32px'};
  transition: background ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};

  @media (max-width: 768px) {
    padding: ${(props) => props.theme?.spacing?.md || '16px'};
  }
`;

const ContentCard = styled.div`
  background: ${(props) =>
    props.theme?.colors?.card?.background ||
    (props.theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${(props) =>
      props.theme?.colors?.card?.border ||
      (props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: ${(props) => props.theme?.borderRadius?.lg || '12px'};
  box-shadow: ${(props) =>
    props.theme?.colors?.card?.shadow ||
    (props.theme?.name === 'dark'
      ? 'rgba(0, 0, 0, 0.2) 0px 1px 3px'
      : 'rgba(0, 0, 0, 0.1) 0px 1px 3px')};
  padding: ${(props) => props.theme?.spacing?.xxl || '48px'};
  max-width: 500px;
  width: 100%;
  text-align: center;
  transition: all ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};

  @media (max-width: 768px) {
    padding: ${(props) => props.theme?.spacing?.xl || '32px'};
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #fa4616 0%, #ff6b3d 100%);
  border-radius: ${(props) => props.theme?.borderRadius?.round || '50%'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${(props) => props.theme?.spacing?.lg || '24px'};
  box-shadow: 0 8px 24px rgba(250, 70, 22, 0.3);
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  svg {
    width: 40px;
    height: 40px;
    color: #ffffff;
    animation: rotate 2s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) =>
    props.theme?.colors?.text?.primary ||
    (props.theme?.name === 'dark' ? '#ffffff' : '#000000')};
  margin: 0 0 ${(props) => props.theme?.spacing?.sm || '8px'} 0;
  transition: color ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Message = styled.p`
  font-size: 15px;
  color: ${(props) =>
    props.theme?.colors?.text?.secondary ||
    (props.theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 0 0 ${(props) => props.theme?.spacing?.xl || '32px'} 0;
  line-height: 1.6;
  transition: color ${(props) => props.theme?.transitions?.theme || '300ms ease-in-out'};
`;

const LoadingWrapper = styled.div`
  margin-top: ${(props) => props.theme?.spacing?.lg || '24px'};
`;

const WaveBars = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const WaveBar = styled.span`
  width: 8px;
  height: 60px;
  background: linear-gradient(180deg, #fa4616 0%, #ff6b3d 100%);
  border-radius: 4px;
  animation: wave 1.2s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || '0s'};

  @keyframes wave {
    0%,
    40%,
    100% {
      transform: scaleY(0.4);
    }
    20% {
      transform: scaleY(1);
    }
  }
`;

/**
 * Handles Stripe Connect onboarding refresh
 * When user exits and needs to restart the onboarding process
 */
export const StripeConnectRefresh = () => {
  const history = useHistory();
  const {
    state: { accountId },
  } = useContext(AppContext);
  const [createStripeOnboardingLink] = useMutation(
    CREATE_STRIPE_ONBOARDING_LINK
  );
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  useEffect(() => {
    let isMounted = true;
    let redirectTimeout = null;

    const refreshOnboarding = async () => {
      try {
        // Get account ID from context

        if (!accountId) {
          if (isMounted) {
            setToaster({
              isOpen: true,
              type: 'error',
              title: 'Account Not Found',
              message: 'Unable to find your account. Redirecting to home...',
            });

            redirectTimeout = setTimeout(() => {
              if (isMounted) {
                history.push('/admin-track/home');
              }
            }, 3000);
          }
          return;
        }

        // Generate new onboarding link
        const result = await createStripeOnboardingLink({
          variables: { account_id: parseInt(accountId) },
        });

        if (!isMounted) return;

        if (result.data?.createStripeOnboardingLink?.url) {
          // Redirect to the new Stripe onboarding URL
          window.location.href = result.data.createStripeOnboardingLink.url;
        } else {
          if (isMounted) {
            setToaster({
              isOpen: true,
              type: 'error',
              title: 'Failed to Generate Link',
              message:
                'Unable to create Stripe onboarding link. Redirecting to home...',
            });

            redirectTimeout = setTimeout(() => {
              if (isMounted) {
                history.push('/admin-track/home');
              }
            }, 3000);
          }
        }
      } catch (error) {
        console.error('Error refreshing Stripe onboarding:', error);

        if (isMounted) {
          setToaster({
            isOpen: true,
            type: 'error',
            title: 'Refresh Failed',
            message: 'Unable to refresh Stripe setup. Redirecting to home...',
          });

          redirectTimeout = setTimeout(() => {
            if (isMounted) {
              history.push('/admin-track/home');
            }
          }, 3000);
        }
      }
    };

    refreshOnboarding();

    return () => {
      isMounted = false;
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [createStripeOnboardingLink, history, accountId]);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <ContentCard>
          <IconWrapper>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </IconWrapper>

          <Title>Refreshing Stripe Setup</Title>
          <Message>
            We're generating a new onboarding link for you. You'll be redirected
            to Stripe in just a moment...
          </Message>

          <LoadingWrapper>
            <WaveBars>
              <WaveBar delay="-0.4s" />
              <WaveBar delay="-0.3s" />
              <WaveBar delay="-0.2s" />
              <WaveBar delay="-0.1s" />
              <WaveBar delay="0s" />
            </WaveBars>
          </LoadingWrapper>
        </ContentCard>

        <Toaster
          isOpen={toaster.isOpen}
          onClose={() => setToaster({ ...toaster, isOpen: false })}
          title={toaster.title}
          message={toaster.message}
          type={toaster.type}
          duration={5000}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};
